import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { components, constants, useServices, useTranslation } from 'cng-web-lib'
import Namespace from 'src/constants/locale/Namespace'
import AceHighwayAbiInbondEventKeys from 'src/constants/locale/key/AceHighwayAbiInbondEvent'
import pathMap from 'src/paths/pathMap'
import AceHwyInbondArrivalApiUrls from 'src/apiUrls/AceHwyInbondArrivalApiUrls'
import NaCodeMaintenanceApiURLs from 'src/apiUrls/NaCodeMaintenanceApiUrls'
import InbondEventViewContent from './InbondEventViewContent'
import ConfirmDialog from '../../common/ConfirmDialog'
import FormProperties from './FormProperties'
import ACEHighwayInbondEventTranslationText from '../contexts/ACEHighwayInbondEventTranslationText'
import ACEHighwayManifestTranslationText from '../contexts/ACEHighwayManifestTranslationText'
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Divider,
} from '@material-ui/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const {
  button: { CngButton },
  table: { useFetchCodeMaintenanceLookup }
} = components

const {
  filter: { EQUAL, IN }
} = constants

function ViewPage(props) {
  const { history, location, showNotification } = props
  const [data, setData] = useState(null)
  const [lookups, setLookups] = useState(null)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const { id } = useParams()
  const { createRecord, deleteRecord, fetchRecord, fetchRecords } =
    useServices()
  const fetchCodeMaintenanceLookup = useFetchCodeMaintenanceLookup()
  const { translate } = useTranslation(Namespace.ACE_HIGHWAY_ABI_INBOND_EVENT)

  const translatedTextsObject = ACEHighwayInbondEventTranslationText()
  const manifestTranslatedTextsObject = ACEHighwayManifestTranslationText()


  let firstExpectedPort;
  if(location !== undefined) {
    firstExpectedPort = location.firstExpectedPort
  }

  useEffect(() => {
    Promise.all([
      // Data
      fetchRecord.execute(
        AceHwyInbondArrivalApiUrls.GET,
        id,
        (res) => res.content[0]
      ),
      // Status
      fetchCodeMaintenanceLookup(
        constants.CodeMaintenanceType.CODE_MASTER,
        undefined,
        [{ field: 'codeType', operator: EQUAL, value: 'ACE_HW_STATUS' }],
        undefined,
        'code'
      ),
      // Action Code
      fetchCodeMaintenanceLookup(
        constants.CodeMaintenanceType.CODE_MASTER,
        undefined,
        [{ field: 'codeType', operator: EQUAL, value: 'ACE_HW_ABI_ACTION' }],
        undefined,
        'code'
      ),
      // MOT
      fetchCodeMaintenanceLookup(
        constants.CodeMaintenanceType.CODE_MASTER,
        undefined,
        [{ field: 'codeType', operator: EQUAL, value: 'ACE_HW_ABI_EXPORT_MOT' }],
        undefined,
        'code'
      ),
      // First expected port
      fetchRecords.execute(
        NaCodeMaintenanceApiURLs.GET,
        {
          filters: [
            { field: 'set_indicator', operator: EQUAL, value1: 'CBP_PORT' }
          ],
          customData: { codeMType: 'DocPortCode' }
        },
        (res) => {
          const result = {}

          res.content.forEach((item) => {
            result[item.code] = item.descriptionEn
          })

          return result
        }
      ),
      // states
      fetchRecords.execute(
        NaCodeMaintenanceApiURLs.GET,
        {
          customData: { codeMType: 'CountryStateCode' }
        },
        (res) => res.content
      )
    ]).then(
      ([
        data,
        status,
        actionCode,
        exportMot,
        portOfArrival,
        states
      ]) => {
        if (data === undefined) {
          showNotification('error', 'Data not found.')
          history.push(pathMap.ABI_INBOND_EVENT_LIST_VIEW)
        }
        setData(data)
        setLookups({
          status,
          actionCode,
          exportMot,
          portOfArrival,
          states
        })
      }
    )
  }, [])

  if (data === null) {
    return <CircularProgress />
  }

  
  function handleClone() {
    createRecord.execute(
      AceHwyInbondArrivalApiUrls.CLONE,
      data,
      (data) => {
        history.push(pathMap.ABI_INBOND_EVENT_ADD_VIEW, data)
      },
      (error) => {
        console.log(error)
      }
    )
  }

  function handleDelete() {
    deleteRecord.execute(
      AceHwyInbondArrivalApiUrls.DELETE,
      data,
      () => {
        showNotification('success', manifestTranslatedTextsObject.deleteSucceeded)
        history.push(pathMap.ABI_INBOND_EVENT_LIST_VIEW)
      },
      (error) => {
        console.log(error)
      }
    )
  }

  function getLookupValue(name, value) {
    if (!lookups) return value

    return lookups[name] && lookups[name][value] ? lookups[name][value] : value
  }

  function getCountryStateLabel(country, state) {
    if (!lookups || !country || !state) return state

    // Check if country code exists in the lookup
    if (Object.keys(lookups.countries).includes(country)) {
      const result = lookups.states[country].find(
        (countryState) => countryState.intlcode === state
      )

      return result ? result.descriptionEn : state
    } else {
      return state
    }
  }

  return (
    <>
      <Card>
        <CardContent>
          <InbondEventViewContent
            data={data}
            getCountryStateLabel={getCountryStateLabel}
            getLookupValue={getLookupValue}
            id={id}
            translatedTextsObject={translatedTextsObject}
          />
        </CardContent>
        <Divider />
        <CardActions style={{ padding: 16, justifyContent: 'space-between' }}>
          <CngButton
            color='secondary'
            onClick={() => history.push(pathMap.ABI_INBOND_EVENT_LIST_VIEW)}
          >
            {translatedTextsObject.backToInbondArrivalExportList}
          </CngButton>
          <Box display='flex' style={{ gap: 16 }}>
            {data.status === '1005' && (
              <CngButton
                disabled={data.status !== '1005'}
                onClick={() => setConfirmDialogOpen(true)}
                startIcon={
                  <FontAwesomeIcon icon={['fal', 'trash']} size='sm' />
                }
                variant='outlined'
              >
                {translatedTextsObject.deleteButton}
              </CngButton>
            )}
            <CngButton
              onClick={handleClone}
              startIcon={<FontAwesomeIcon icon={['fal', 'copy']} size='sm' />}
              variant='outlined'
            >
              {translatedTextsObject.cloneButton}
            </CngButton>
            {data.status !== '1008' && (
              <CngButton
                color='primary'
                disabled={data.status === '1008'}
                startIcon={<FontAwesomeIcon icon={['fal', 'pen']} size='sm' />}
                onClick={() =>
                  history.push(`${pathMap.ABI_INBOND_EVENT}/edit/${id}`)
                }
              >
                {translatedTextsObject.editButton}
              </CngButton>
            )}
          </Box>
        </CardActions>
      </Card>
      <ConfirmDialog
        isConfirmDialogOpen={confirmDialogOpen}
        closeDialog={() => setConfirmDialogOpen(false)}
        confirmDialog={handleDelete}
        content={manifestTranslatedTextsObject.deleteConfirmationMsg}
        okMsg={manifestTranslatedTextsObject.deleteConfirmButton}
        cancelMsg={manifestTranslatedTextsObject.deleteCancelButton}
        title={translatedTextsObject.deleteButton}
      />
    </>
  )
}

export default ViewPage
